import React, { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Modal from 'fast-modal-library';
import { useSelector } from 'react-redux';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

export const isSpreadsheetFile = (fileType) => {
    return Boolean(fileType && fileType.includes('spreadsheetml'));
};

export const formatYesNo = (value) => (value ? 'Oui' : 'Non');

export const parseExcelContent = (fileContent, cleanData) => {
    const workbook = XLSX.read(fileContent, { type: 'binary' });
    const worksheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[worksheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const nonEmptyRows = jsonData.filter(row => row.some(item => item));
    return cleanData(nonEmptyRows);
};

const ImportCsvExcel = () => {
    const Numberapp = useSelector((state) => state.app.appNumber);
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(1);
    const [hasErrors, setHasErrors] = useState(false);

    const hide = () => {
        setShowModal(false);
        setData([]);
        window.location.reload();
    };

    const sanitizeInput = (input) => {
        if (typeof input !== 'string') return input;

        const replacements = {
            "'": ' ',
            "é": 'e',
            "è": 'e',
            "ê": 'e',
            "à": 'a',
            "ù": 'u',
            "ç": 'c',
            "ö": 'o',
            "î": 'i',
        };

        return input.replace(/./g, (char) => replacements[char] || char);
    };

    const cleanData = (data) => {
        return data.map(row =>
            row.map(cell => {
                if (typeof cell === 'string') {
                    const normalizedCell = cell.normalize("NFC");
                    return sanitizeInput(normalizedCell);
                }
                return cell;
            })
        );
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        const reader = new FileReader();

        reader.onload = async (evt) => {
            let fileContent;

            if (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel') {
                const textDecoder = new TextDecoder("utf-8");
                fileContent = textDecoder.decode(new Uint8Array(evt.target.result));
            } else {
                fileContent = evt.target.result;
            }

            if (file.type === 'text/csv') {
                Papa.parse(fileContent, {
                    complete: (results) => {
                        const nonEmptyRows = results.data.filter(row => row.some(item => item));
                        const cleanedData = cleanData(nonEmptyRows);
                        setData(oldData => [...oldData, ...cleanedData]);
                    },
                    skipEmptyLines: true,
                    encoding: "UTF-8",
                });
            } else {
                /* istanbul ignore else */
                if (isSpreadsheetFile(file.type)) {
                    const cleanedData = parseExcelContent(fileContent, cleanData);
                    setData(oldData => [...oldData, ...cleanedData]);
                }
            }
        };

        reader.readAsArrayBuffer(file);
    };

    /* istanbul ignore next */
    const handleRegisterUsers = async (startIndex = 1) => {
        let errors = false; // Local error tracking
        for (let i = startIndex; i < data.length; i++) {
            const row = data[i];
            const [username, password, firstname, lastname, company, siret, chefEntreprise, active] = row;
    
            const formData = {
                username,
                password,
                firstname: sanitizeInput(firstname),
                lastname: sanitizeInput(lastname),
                company: sanitizeInput(company),
                siret,
                chefEntreprise: chefEntreprise === 'true' || chefEntreprise === true,
                active: active === 'true' || active === true,
                appNumber: Numberapp,
            };
    
            try {
                const response = await fetch(apiPrefix + 'register', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                });
    
                if (response.status === 409) {
                    console.warn(`Conflit détecté : L'utilisateur ${username} existe déjà.`);
                    setCurrentUser(formData); // Stocke les informations pour la modification
                    setShowConfirmModal(true); // Affiche la modale de confirmation
                    setCurrentIndex(i + 1); // Enregistre l'index pour continuer après confirmation
                    return; // Arrête la boucle pour attendre la confirmation
                }
    
                if (!response.ok) {
                    const errorMessage = await response.json();
                    console.error('Erreur lors de l\'inscription:', errorMessage.error);
                    errors = true;
                } else {
                    console.log(`Utilisateur ${username} inscrit avec succès.`);
                }
            } catch (error) {
                console.error('Erreur lors de la tentative d\'inscription:', error);
                errors = true;
            }
        }
    
        if (!errors) {
            setShowModal(true); // Affiche la modale uniquement si aucune erreur n'est survenue
        } else {
            console.error('Des erreurs sont survenues lors du traitement des utilisateurs.');
        }
    };
    
    const handleConfirmUpdate = async (confirm) => {
        if (confirm && currentUser) {
            const sanitizedUser = {
                ...currentUser,
                firstname: sanitizeInput(currentUser.firstname),
                lastname: sanitizeInput(currentUser.lastname),
                company: sanitizeInput(currentUser.company),
            };
    
            try {
                const response = await fetch(apiPrefix + 'admin/updateimport', {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(sanitizedUser),
                });
    
                if (response.ok) {
                    console.log(`Utilisateur ${currentUser.username} mis à jour avec succès.`);
                } else {
                    const errorMessage = await response.json();
                    console.error('Erreur lors de la mise à jour de l\'utilisateur:', errorMessage.error);
                }
            } catch (error) {
                console.error('Erreur lors de la mise à jour:', error);
            }
        }
    
        setCurrentUser(null);
        setShowConfirmModal(false);
    
        // Reprendre la boucle après la confirmation
        await handleRegisterUsers(currentIndex);
    };
    
    

    return (
        <div className="space-y-6">
            <div className="card-theme">
                <h3 className="text-lg font-semibold text-theme-primary mb-4">
                    <i className="fas fa-file-import mr-2 text-theme-accent"></i>
                    Import de fichiers CSV/Excel
                </h3>
                
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sélectionner un fichier
                    </label>
                    <input 
                        type="file" 
                        accept=".csv, .xlsx" 
                        onChange={handleFileUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-theme-accent file:text-white hover:file:bg-theme-accent-active"
                    />
                </div>
                
                {data.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                <i className="fas fa-info-circle mr-2 text-theme-accent"></i>
                                {data.length} lignes détectées
                            </p>
                            <button 
                                onClick={() => handleRegisterUsers(1)}
                                className="btn-theme"
                            >
                                <i className="fas fa-user-plus mr-2"></i>
                                Inscrire les utilisateurs
                            </button>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mot de passe</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prénom</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entreprise</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SIRET</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chef d'entreprise</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actif</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data.map((row, rowIndex) => (
                                        <tr key={rowIndex} className="hover:bg-gray-50">
                                            {row.map((item, itemIndex) => (
                                                <td key={itemIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {typeof item === 'string' ? sanitizeInput(item) : item}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
            
            {showModal && (
                <Modal contentModal="Inscription réussie !" hide={hide} />
            )}
            
            {showConfirmModal && (
                <Modal 
                    contentModal={
                        <div className="p-6">
                            <div className="mb-4">
                                <i className="fas fa-exclamation-triangle text-yellow-500 text-2xl mb-2"></i>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Utilisateur existant détecté
                                </h3>
                                <p className="text-gray-600">
                                    L'utilisateur suivant existe déjà. Voulez-vous mettre à jour ses informations ?
                                </p>
                            </div>
                            
                            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div><strong>Email:</strong> {currentUser?.username}</div>
                                    <div><strong>Mot de passe:</strong> {currentUser?.password}</div>
                                    <div><strong>Prénom:</strong> {currentUser?.firstname}</div>
                                    <div><strong>Nom:</strong> {currentUser?.lastname}</div>
                                    <div><strong>Entreprise:</strong> {currentUser?.company}</div>
                                    <div><strong>SIRET:</strong> {currentUser?.siret}</div>
                                    <div><strong>Chef d'Entreprise:</strong> {formatYesNo(currentUser?.chefEntreprise)}</div>
                                    <div><strong>Actif:</strong> {formatYesNo(currentUser?.active)}</div>
                                </div>
                            </div>
                            
                            <div className="flex justify-end space-x-3">
                                <button 
                                    onClick={() => handleConfirmUpdate(false)}
                                    className="btn-theme-secondary"
                                >
                                    <i className="fas fa-times mr-2"></i>
                                    Annuler
                                </button>
                                <button 
                                    onClick={() => handleConfirmUpdate(true)}
                                    className="btn-theme"
                                >
                                    <i className="fas fa-check mr-2"></i>
                                    Confirmer la mise à jour
                                </button>
                            </div>
                        </div>
                    }
                    hide={() => setShowConfirmModal(false)}
                />
            )}
        </div>
    );
};

export default ImportCsvExcel;
